import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  addOrderProduct,
  updateOrderProductQuantity,
  removeOrderProductDetail,
  removeOrderPhoto,
  createOrder,
  setDeliveryType,
  createOrderReceipt,
} from "src/api/order.api";
import { orderQueryKey } from "./useOrder";
import { GetOrderDto, CreateUserOrderDTO, UpdateDeliveryTypeDto } from "src/services/generated-api";
import { setGuestTokenCookies } from "src/services/api";
import * as Sentry from "@sentry/nextjs";

type CreateOrderPayload = Omit<CreateUserOrderDTO, "photoSizeId"> & {
  photoSizeId?: string | null;
};

export function useCreateOrder() {
  const queryClient = useQueryClient();

  return useMutation<GetOrderDto, Error, CreateOrderPayload>({
    mutationFn: createOrder,
    onSuccess: (order) => {
      const orderAny = order as GetOrderDto & { guestToken?: string };
      if (orderAny.guestToken) {
        setGuestTokenCookies(orderAny.guestToken);
      }
      queryClient.setQueryData(orderQueryKey(order.id), order);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
}

interface AddProductVariables {
  orderId: string;
  productId: string;
  quantity: number;
  guestToken: string;
}

export function useAddOrderProduct() {
  const queryClient = useQueryClient();

  return useMutation<GetOrderDto, Error, AddProductVariables>({
    mutationFn: ({ orderId, productId, quantity, guestToken }) =>
      addOrderProduct(orderId, { productId, quantity }, guestToken),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData(orderQueryKey(updatedOrder.id), updatedOrder);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
}

interface UpdateProductQtyVariables {
  orderId: string;
  orderProductDetailId: string;
  quantity: number;
  guestToken: string;
}

export function useUpdateOrderProductQuantity() {
  const queryClient = useQueryClient();

  return useMutation<GetOrderDto, Error, UpdateProductQtyVariables>({
    mutationFn: ({ orderId, orderProductDetailId, quantity, guestToken }) =>
      updateOrderProductQuantity(orderId, { orderProductDetailId, quantity }, guestToken),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData(orderQueryKey(updatedOrder.id), updatedOrder);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
}

interface RemoveProductDetailVariables {
  orderId: string;
  orderProductDetailId: string;
  guestToken: string;
}

export function useRemoveOrderProductDetail() {
  const queryClient = useQueryClient();

  return useMutation<GetOrderDto, Error, RemoveProductDetailVariables>({
    mutationFn: ({ orderId, orderProductDetailId, guestToken }) =>
      removeOrderProductDetail(orderId, { orderProductDetailId }, guestToken),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData(orderQueryKey(updatedOrder.id), updatedOrder);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
}

interface RemoveOrderPhotoVariables {
  orderId: string;
  orderPhotoId: string;
  guestToken: string;
}

export function useRemoveOrderPhoto() {
  const queryClient = useQueryClient();

  return useMutation<GetOrderDto, Error, RemoveOrderPhotoVariables>({
    mutationFn: ({ orderId, orderPhotoId, guestToken }) =>
      removeOrderPhoto(orderId, orderPhotoId, guestToken),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData(orderQueryKey(updatedOrder.id), updatedOrder);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
}

interface UpdateDeliveryTypeVariables {
  orderId: string;
  payload: UpdateDeliveryTypeDto;
  guestToken: string;
}

export function useSetDeliveryType() {
  const queryClient = useQueryClient();

  return useMutation<GetOrderDto, Error, UpdateDeliveryTypeVariables>({
    mutationFn: ({ orderId, payload, guestToken }) =>
      setDeliveryType(orderId, payload, guestToken),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData(orderQueryKey(updatedOrder.id), updatedOrder);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
}

interface CreateReceiptVariables {
  orderId: string;
  guestToken: string;
}

export function useCreateOrderReceipt() {
  const queryClient = useQueryClient();

  return useMutation<GetOrderDto, Error, CreateReceiptVariables>({
    mutationFn: ({ orderId, guestToken }) => createOrderReceipt(orderId, guestToken),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData(orderQueryKey(updatedOrder.id), updatedOrder);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
}
